import {Delivery} from "../common/GetDeliveriesDetailsGQLResponse";
import {AggregateCablingManifestBySite, AggregatedCablingStatus} from "../plannedDeliveries/interface/AggregateCablingManifestBySite";
import {CABLING_STATUS_ENUM} from "../LmdpConstants";
import moment from "moment-timezone";

/**
 *
 * Populate the AggregateCablingForecastBySite instance with week over week data
 * It has been assumed that AggregateCablingForecastBySite instance would contain aggregatedCablingStatus array with length = numberOfWeeks
 *
 * @see #getAggregateCablingStatusForWeeks()
 *
 * @param delivery
 * @param aggregateCablingForecastStatusBySite
 *
 * @returns AggregatedCablingStatus[]
 */
export function populateCablingStatus(delivery: Delivery, aggregateCablingForecastStatusBySite: AggregateCablingManifestBySite): AggregatedCablingStatus[] {
    const currentIsoWeekStart = moment().tz('UTC').startOf('isoWeek'); // Start of the current ISO week
    const deliveryDateIsoWeekStart = delivery.calculatedDeliveryDate?.startDate
        ? moment(delivery.calculatedDeliveryDate.startDate).tz('UTC').startOf('isoWeek') // Start of the delivery week
        : null;

    if (deliveryDateIsoWeekStart === null) {
        return aggregateCablingForecastStatusBySite.aggregatedCablingStatus;
    }
    const weekIndex = deliveryDateIsoWeekStart.diff(currentIsoWeekStart, 'weeks');    

    if (weekIndex >= 0 && weekIndex < aggregateCablingForecastStatusBySite.aggregatedCablingStatus.length) {
        const status = aggregateCablingForecastStatusBySite.aggregatedCablingStatus[weekIndex];
        if (status) {
            if (delivery.cablingStatus === CABLING_STATUS_ENUM.IN_PROGRESS) {
                status.cablingInProgress++;
            } else if (delivery.cablingStatus === CABLING_STATUS_ENUM.CABLED) {
                status.cablingDone++;
            } else {
                status.cablingNotStarted++;
            }
            status.totalRacksGettingDelivered++;
        }
    }
    return aggregateCablingForecastStatusBySite.aggregatedCablingStatus;
}

/**
 *
 * Provides a Map with key = cluster_site mapped to respective delivery
 *
 * @param deliveryList
 * @returns Map<String, Delivery[]>
 */
export function getMapOfClusterPhysicalSiteAndDeliveriesDetailData(deliveryList: Delivery[]): Map<string, Delivery[]> {
    let mapOfClusterPhysicalSiteAndDeliveriesDetailsList: Map<string, Delivery[]> = new Map<string, Delivery[]>();

    if (deliveryList === null || deliveryList.length === 0) {
        return mapOfClusterPhysicalSiteAndDeliveriesDetailsList;
    }

    deliveryList.forEach((delivery: Delivery) => {
        const cluster = delivery.cluster || '-';
        const site = delivery.site || '-';

        const key = cluster + '_' + site;

        if (mapOfClusterPhysicalSiteAndDeliveriesDetailsList.has(key)) {
            mapOfClusterPhysicalSiteAndDeliveriesDetailsList.get(key)?.push(delivery)
        } else {
            mapOfClusterPhysicalSiteAndDeliveriesDetailsList.set(key, [delivery]);
        }
    });

    return mapOfClusterPhysicalSiteAndDeliveriesDetailsList;
}

/**
 * This method takes the DeliveryDetailList and maps it to AggregateCablingForecastBySite aggregating based on (cluster, physical site) over a week of span
 *
 * @see #getMapOfClusterPhysicalSiteAndDeliveriesDetailData()
 *
 * @param deliveryList
 * @param numberOfWeeks
 */
export function getAggregateCablingStatusForWeeks(deliveryList: Delivery[], numberOfWeeks: number): AggregateCablingManifestBySite[] {

    let mapOfClusterPhysicalSiteAndDeliveriesDetailsList: Map<string, Delivery[]> = getMapOfClusterPhysicalSiteAndDeliveriesDetailData(deliveryList);

    let aggregateCablingForecastStatusBySiteList: AggregateCablingManifestBySite[] = [];


    mapOfClusterPhysicalSiteAndDeliveriesDetailsList.forEach((deliveryListForSite: Delivery[], key: string) => {


        let cluster: string = key.split('_')[0];
        let site = key.split('_')[1];

        let aggregateCablingForecastStatusBySite: AggregateCablingManifestBySite = {
            cluster: cluster,
            site: site,
            totalCablingDone: 0,
            totalNotDone: 0,
            aggregatedCablingStatus: Array.from({length: numberOfWeeks}, u => ({
                cablingInProgress: 0,
                cablingDone: 0,
                cablingNotStarted: 0,
                totalRacksGettingDelivered: 0
            }))

        };

        deliveryListForSite.forEach((delivery: Delivery) => {
            let deliveredToTime: number | undefined = delivery.calculatedDeliveryDate.startDate;
            if (deliveredToTime === undefined) {
                return;
            }
            aggregateCablingForecastStatusBySite.aggregatedCablingStatus =
                populateCablingStatus(delivery, aggregateCablingForecastStatusBySite);
        });

        aggregateCablingForecastStatusBySiteList.push(aggregateCablingForecastStatusBySite);
    });

    return aggregateCablingForecastStatusBySiteList;


}